import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Avatar } from "../components/Avatar.js";
import { FranchiseBadge } from "../components/FranchiseBadge.js";
import {
  auctionLotLabel,
  currentLot,
  fetchPoolState,
  lotProgress,
  playerFacts,
} from "../lib/auction.js";
import { hasRole, useAuth } from "../lib/auth.js";
import type { AuctionCurrency } from "../lib/currency.js";
import { fetchFranchises, type FranchiseSetup } from "../lib/franchises.js";
import { fetchTournaments } from "../lib/tournaments.js";
import type { AuctionPoolState, TournamentEvent } from "../lib/types.js";
import { auctionStateKey, usePeople } from "./auction/parts.js";

/**
 * How often the board asks again. It sits on a projector nobody touches, so it keeps asking in
 * the background, as the court board does; a realtime channel would be one more thing to keep
 * alive for a gain nobody in the room could see.
 */
const BOARD_REFRESH_MS = 2000;

/** How long the SOLD moment holds the screen before the next player shows through. */
const SOLD_MS = 4500;

type Sale = { memberId: string; squadId: string; price: number | null };

/**
 * The room's screen, Floodlit like the court board because it hangs at the far end of a hall:
 * the player up with their face and lot, the bid on the highlighter with the team holding it,
 * and every franchise's logo, money left and squad so far. It follows whichever pool is open, so
 * nobody touches it between pools. Organisers only, like everything about the auction.
 */
export function AuctionBoard() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const { data: tournaments } = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);
  const events = tournament?.events ?? [];
  const states = useQueries({
    queries: events.map((event) => ({
      queryKey: auctionStateKey(event.id),
      queryFn: () => fetchPoolState(event.id),
      refetchInterval: BOARD_REFRESH_MS,
      refetchIntervalInBackground: true,
    })),
  });
  const { data: setup } = useQuery({
    queryKey: ["franchises", tournamentId],
    queryFn: () => fetchFranchises(tournamentId!),
    enabled: Boolean(tournamentId),
  });

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }

  const index = states.findIndex((s) => s.data?.open);
  const state = index >= 0 ? states[index]!.data! : null;
  const event = index >= 0 ? events[index]! : null;

  return (
    <div className="floodlit min-h-dvh">
      <main className="mx-auto max-w-[1600px] px-8 py-6">
        <div className="mb-6 flex items-baseline justify-between gap-4">
          <p className="micro">{tournament?.name ?? ""}</p>
          {tournament ? (
            <Link
              to={`/tournaments/${tournament.id}/auction${event ? `/${event.id}` : ""}`}
              className="micro underline"
            >
              Console
            </Link>
          ) : null}
        </div>
        {!tournament || !setup ? (
          <p className="micro">Loading…</p>
        ) : !state || !event ? (
          <p className="font-display mt-24 text-center text-6xl">No pool is open</p>
        ) : (
          <Live event={event} state={state} setup={setup} currency={tournament.auctionCurrency} />
        )}
      </main>
    </div>
  );
}

function Live({
  event,
  state,
  setup,
  currency,
}: {
  event: TournamentEvent;
  state: AuctionPoolState;
  setup: FranchiseSetup;
  currency: AuctionCurrency;
}) {
  const people = usePeople(event, setup, currency);
  const lot = currentLot(state);
  const up = state.up;
  const who = up ? people.person(up.memberId) : null;
  const holder = up ? state.squads.find((s) => s.squadId === up.holderId) : undefined;
  const holderFranchise = holder ? people.franchise(holder.franchiseId) : undefined;
  const { sale, arrived } = useSale(state);

  return (
    <>
      {sale ? <SoldMoment key={sale.memberId} sale={sale} state={state} people={people} /> : null}
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="font-display text-5xl">{event.name}</h1>
        {lot ? (
          <p className="font-mono text-lg text-ink-muted">
            {auctionLotLabel(lot)} · base {people.price(lot.basePrice ?? 0)} ·{" "}
            {lotProgress(state, lot.lotId).taken} of {lot.size} taken
          </p>
        ) : null}
      </div>

      <section
        className="my-10 flex flex-wrap items-center justify-center gap-10"
        aria-live="polite"
        aria-label="Up now"
      >
        {up && who ? (
          <>
            <Avatar name={who.name} url={who.avatarUrl} size={220} />
            <div className="min-w-0">
              {up.offers > 0 ? (
                <p className="font-mono text-lg uppercase text-ink-muted">Offered again</p>
              ) : null}
              <p className="font-display text-8xl leading-[0.9]">{who.name}</p>
              {playerFacts(up.details).length > 0 ? (
                <ul className="mt-4 flex flex-wrap gap-3" aria-label="About the player">
                  {playerFacts(up.details).map((fact) => (
                    <li
                      key={fact}
                      className="font-mono px-3 py-1 text-xl"
                      style={{ border: "1px solid var(--color-line-muted)" }}
                    >
                      {fact}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-6 flex items-center gap-6">
                <span className="font-display text-9xl leading-none">
                  <span className={up.bid === null ? "" : "highlight px-4"}>
                    {people.price(up.bid ?? up.nextBid)}
                  </span>
                </span>
                <span className="flex items-center gap-3 text-3xl font-bold">
                  {holder ? (
                    <>
                      {holderFranchise ? (
                        <FranchiseBadge franchise={holderFranchise} size={64} />
                      ) : null}
                      {people.franchiseName(holder.franchiseId)}
                    </>
                  ) : (
                    <span className="text-ink-muted">
                      {up.forced ? `Only ${up.forced.name} can take this player` : "Opening bid"}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </>
        ) : (
          <p className="font-display text-6xl text-ink-muted">Next player coming up</p>
        )}
      </section>

      <section
        className="grid gap-5"
        style={{
          gridTemplateColumns: `repeat(${Math.min(state.squads.length, 4)}, minmax(0, 1fr))`,
        }}
        aria-label="Teams"
      >
        {state.squads.map((squad) => {
          const franchise = people.franchise(squad.franchiseId);
          const holding = up?.holderId === squad.squadId;
          const players = state.players.filter((p) => p.squadId === squad.squadId);
          const captain = squad.captainId ? people.person(squad.captainId) : null;
          return (
            <div
              key={squad.squadId}
              className="p-4"
              style={{
                background: "var(--color-surface)",
                borderTop: `6px solid ${franchise?.colour ?? "var(--color-line)"}`,
                outline: holding ? "3px solid var(--color-highlight)" : undefined,
              }}
            >
              <div className="flex items-center gap-3">
                {franchise ? <FranchiseBadge franchise={franchise} size={56} /> : null}
                <span className="min-w-0 flex-1 text-2xl font-extrabold leading-tight">
                  {people.franchiseName(squad.franchiseId)}
                </span>
              </div>
              <p className="font-display mt-3 text-5xl leading-none">
                {people.price(squad.left)}
                <span className="font-mono ml-2 text-base text-ink-muted">left</span>
              </p>
              <ul className="mt-4 flex flex-col gap-2 text-lg">
                {captain ? (
                  <li className="flex items-center gap-3">
                    <Avatar name={captain.name} url={captain.avatarUrl} size={40} />
                    <span className="min-w-0 flex-1 truncate font-semibold">{captain.name}</span>
                    <span className="micro">captain</span>
                  </li>
                ) : null}
                {players.map((p) => {
                  const player = people.person(p.memberId);
                  return (
                    <li
                      key={p.memberId}
                      className={`-mx-2 flex items-center gap-3 px-2 ${
                        arrived === p.memberId ? "sold-arrive" : ""
                      }`}
                    >
                      <Avatar name={player.name} url={player.avatarUrl} size={40} />
                      <span className="min-w-0 flex-1 truncate">{player.name}</span>
                      {p.price !== null ? (
                        <span className="font-mono text-base">{people.price(p.price)}</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </section>
    </>
  );
}

/**
 * The sale the board is celebrating, if any, and the player it last saw sold, whose row in
 * their new team lights up once the moment clears. A sale is a new `lastEntry` of kind sold
 * seen between two polls; whatever the board found when it opened is old news and stays quiet.
 */
function useSale(state: AuctionPoolState): { sale: Sale | null; arrived: string | null } {
  const entry = state.lastEntry;
  const key = entry ? `${entry.kind}:${entry.memberId}:${entry.squadId}:${entry.price}` : "";
  const seen = useRef(key);
  const [sale, setSale] = useState<Sale | null>(null);
  const [arrived, setArrived] = useState<string | null>(null);

  useEffect(() => {
    if (key === seen.current) return;
    seen.current = key;
    if (entry?.kind !== "sold" || !entry.squadId) return;
    setSale({
      memberId: entry.memberId,
      squadId: entry.squadId,
      price: entry.price,
    });
    setArrived(entry.memberId);
  }, [key, entry]);

  useEffect(() => {
    if (!sale) return;
    const timer = setTimeout(() => setSale(null), SOLD_MS);
    return () => clearTimeout(timer);
  }, [sale]);

  return { sale, arrived };
}

function SoldMoment({
  sale,
  state,
  people,
}: {
  sale: Sale;
  state: AuctionPoolState;
  people: ReturnType<typeof usePeople>;
}) {
  const who = people.person(sale.memberId);
  const squad = state.squads.find((s) => s.squadId === sale.squadId);
  const franchise = squad ? people.franchise(squad.franchiseId) : undefined;
  const colour = franchise?.colour ?? "var(--color-highlight)";

  return (
    <div
      className="sold-scrim fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 overflow-hidden"
      style={
        {
          background: "var(--color-ground)",
          "--sold-ms": `${SOLD_MS}ms`,
        } as CSSProperties
      }
      role="status"
      aria-label={`${who.name} sold${squad ? ` to ${people.franchiseName(squad.franchiseId)}` : ""}`}
    >
      <div
        className="sold-sweep absolute inset-x-0 top-0 h-6"
        style={{ background: colour }}
        aria-hidden
      />
      <div
        className="sold-sweep absolute inset-x-0 bottom-0 h-6"
        style={{ background: colour, animationDelay: "120ms" }}
        aria-hidden
      />
      <div className="sold-rise relative">
        <Avatar name={who.name} url={who.avatarUrl} size={260} />
        <span
          className="sold-stamp font-display absolute -right-24 -top-6 px-6 py-1 text-8xl tracking-wider"
          style={{
            border: "8px solid var(--color-attention-fill)",
            color: "var(--color-attention-fill)",
          }}
          aria-hidden
        >
          SOLD
        </span>
      </div>
      <p
        className="sold-rise font-display text-center text-9xl leading-[0.9]"
        style={{ animationDelay: "150ms" }}
      >
        {who.name}
      </p>
      <div
        className="sold-rise flex flex-wrap items-center justify-center gap-8"
        style={{ animationDelay: "300ms" }}
      >
        {sale.price !== null ? (
          <span className="font-display highlight px-5 text-9xl leading-none">
            {people.price(sale.price)}
          </span>
        ) : null}
        {squad ? (
          <span className="flex items-center gap-4 text-5xl font-extrabold">
            {franchise ? <FranchiseBadge franchise={franchise} size={96} /> : null}
            {people.franchiseName(squad.franchiseId)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
